import { useState } from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Switch,
  Box,
  IconButton,
  Chip,
  Stack,
  Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface FieldCondition {
  field_name: string;
  field_value: string;
}

interface EmailTemplateSegmentConfig {
  segment_id: string;
  segment_name: string;
  allowed_message_types: string[];
  auto_approval: boolean;
  field_conditions?: FieldCondition[];
}

interface TemplateGenerationTableProps {
  emailTemplatesConfig: EmailTemplateSegmentConfig[];
  loading: boolean;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onAutoApprovalToggle: (index: number, value: boolean) => void;
  ariaLabel?: string;
}

function ChipsCell({ items }: { items: string[] | undefined }) {
  if (!items || !items.length) {
    return (
      <Typography component="span" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
      {items.map(label => (
        <Chip
          key={label}
          label={label.replace(/_/g, ' ')}
          size="small"
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function FieldConditionsRow({
  open,
  conditions,
  colSpan,
}: {
  open: boolean;
  conditions: FieldCondition[];
  colSpan: number;
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ mx: 2, my: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', mb: 1, display: 'block' }}
            >
              Field Conditions
            </Typography>
            {conditions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No field conditions configured.
              </Typography>
            ) : (
              <Table size="small" sx={{ width: 'auto', minWidth: 320 }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50', py: 0.75 } }}>
                    <TableCell sx={{ width: 180 }}>Field Name</TableCell>
                    <TableCell>Field Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conditions.map((cond, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Chip
                          label={cond.field_name}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={!cond.field_value ? 'text.secondary' : undefined}>
                          {cond.field_value ||
                            (cond.field_name?.toUpperCase() === 'CATCHALL' ? '—' : '')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}

export const TemplateGenerationTable = ({
  emailTemplatesConfig,
  loading,
  onEdit,
  onDelete,
  onAutoApprovalToggle,
  ariaLabel = 'template generation config',
}: TemplateGenerationTableProps) => {
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const toggleRow = (index: number) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const COL_SPAN = 5;

  return (
    <Box sx={{ overflow: 'auto', width: '100%' }}>
      <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }} />
            <TableCell>Segment Name</TableCell>
            <TableCell>Allowed Message Types</TableCell>
            <TableCell>Auto Approval</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(emailTemplatesConfig || []).map((row, segmentIndex) => {
            const isOpen = Boolean(expandedRows[segmentIndex]);
            const conditions = row.field_conditions || [];
            const hasConditions = conditions.length > 0;

            return (
              <>
                <TableRow
                  key={`row-${row.segment_id || segmentIndex}`}
                  sx={{
                    '& > td': { borderBottom: isOpen ? 'none' : undefined },
                    cursor: 'default',
                  }}
                >
                  <TableCell sx={{ pr: 0, width: 40 }}>
                    <IconButton
                      size="small"
                      onClick={() => toggleRow(segmentIndex)}
                      aria-label={isOpen ? 'collapse row' : 'expand row'}
                    >
                      {isOpen ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon
                          fontSize="small"
                          color={hasConditions ? 'primary' : 'disabled'}
                        />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell component="th" scope="row" sx={{ width: 220 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>{row.segment_name || row.segment_id || '—'}</span>
                      {hasConditions && (
                        <Chip
                          label={conditions.length}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ width: 360 }}>
                    <ChipsCell items={row.allowed_message_types} />
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <Switch
                      checked={Boolean(row.auto_approval)}
                      onChange={e =>
                        onAutoApprovalToggle(segmentIndex, e.target.checked)
                      }
                      disabled={loading}
                      inputProps={{ 'aria-label': 'auto approval' }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 90 }}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => onEdit(segmentIndex)}
                      disabled={loading}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => onDelete(segmentIndex)}
                      disabled={loading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <FieldConditionsRow
                  key={`conditions-${row.segment_id || segmentIndex}`}
                  open={isOpen}
                  conditions={conditions}
                  colSpan={COL_SPAN}
                />
              </>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
